/**
 * @fileoverview Logout-all-sessions mutation hook.
 * @description Wraps POST /identity/logout-all, which revokes every live session the caller holds
 * — including the current one — so a successful call always clears the local session and returns
 * to /login, the same way as a regular logout.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router'

import { logoutAllSessions } from '@/features/auth/services/session.service'
import { ROUTES } from '@/shared/constants/routes.constants'
import { useAuth } from '@/shared/lib/auth/useAuth'

/**
 * @description Revokes every session the caller holds, then clears the local session and navigates
 * to /login.
 * @returns {ReturnType<typeof useMutation>} The mutation object (mutate, isPending, ...).
 */
export function useLogoutAllSessions() {
  const { logout } = useAuth()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: logoutAllSessions,
    onSuccess: () => {
      logout()
      void navigate(ROUTES.LOGIN, { replace: true })
    },
  })
}
